import { useEffect, useEffectEvent, useRef } from 'react';
import type { TrackingEvents } from './types.js';
import { useTrack } from './useTrack.js';

/**
 * Reports a view event once per mount. The ref, not the deps, is what holds the line: StrictMode runs
 * every effect twice in dev. A real remount gets a fresh ref and reports again.
 *
 * `enabled` defers the report until the page has something to report; it never re-arms.
 */
export function useTrackOnce<E extends keyof TrackingEvents>(event: E, params: TrackingEvents[E], enabled = true): void {
  const track = useTrack();
  const report = useEffectEvent(() => track(event, params));
  const reported = useRef(false);

  useEffect(() => {
    if (!enabled || reported.current) {
      return;
    }
    reported.current = true;
    report();
  }, [enabled]);
}
